"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.fillDataToCell = void 0;
const command_manager_1 = require("../../command_manager");
const commands_1 = require("../../commands");
const lodash_1 = require("lodash");
const model_1 = require("../../model");
const store_1 = require("../../exports/store");
const types_1 = require("../../types");
const utils_1 = require("../../utils");
const set_field_attr_1 = require("./set_field_attr");
const set_records_1 = require("./set_records");
const dayjs_1 = __importDefault(require("dayjs"));
const EARLIEST_DATE = -2177395200000; // time only supports up to 1901-01-01
const DELTA = 1e-12; // Tolerance when comparing floating-point numbers, EPSILON precision is too high
function patternFinder(selectCellCol, fieldType, state, snapshot, field) {
    if (selectCellCol.length < 2)
        return { type: 'copy' };
    const nums = [];
    selectCellCol.forEach((selectCell) => {
        let cellValue = store_1.Selectors.getCellValue(state, snapshot, selectCell.recordId, selectCell.fieldId);
        cellValue = (0, model_1.handleEmptyCellValue)(cellValue, model_1.Field.bindContext(field, state).basicValueType);
        nums.push(cellValue);
    });
    if (nums.find(n => n === null) === null) {
        return { type: 'copy' };
    }
    if (fieldType === types_1.FieldType.Number) {
        if (Math.max(...nums) > (1e16 - 1)) {
            return { type: 'copy' };
        }
        const diff = nums[1] - nums[0];
        for (let i = 2; i < nums.length; i++) {
            const newDiff = nums[i] - nums[i - 1];
            if (Math.abs(newDiff - diff) > DELTA) {
                return { type: 'copy' };
            }
        }
        return { type: 'arithmetic', args: { diff: diff } };
    }
    else if (fieldType === types_1.FieldType.DateTime) {
        const { property: { includeTime, dateFormat } } = field;
        const _nums = includeTime ? nums : nums.map(utils_1.getDate);
        // The format of the time is different without the corresponding calculation logic
        if (includeTime) { // When the specific point is reached
            const diff = _nums[1] - _nums[0];
            for (let i = 2; i < _nums.length; i++) {
                const newDiff = _nums[i] - _nums[i - 1];
                if (Math.abs(newDiff - diff) > DELTA) {
                    return { type: 'copy' };
                }
            }
            return { type: 'dateArithmetic', args: { dDiff: diff, dUnit: 'time' } };
        }
        if ([types_1.DateFormat['YYYY/MM/DD'], types_1.DateFormat['YYYY-MM-DD'], types_1.DateFormat['DD/MM/YYYY'],
            types_1.DateFormat['MM-DD'], types_1.DateFormat['DD']].includes(dateFormat)) { // When the date arrives, the filled date defaults to 0:00
            const diff = (0, dayjs_1.default)(_nums[1]).diff(_nums[0], 'day');
            for (let i = 2; i < _nums.length; i++) {
                const newDiff = (0, dayjs_1.default)(_nums[i]).diff(_nums[i - 1], 'day');
                if (Math.abs(newDiff - diff) > DELTA) {
                    return { type: 'copy' };
                }
            }
            return { type: 'dateArithmetic', args: { dDiff: diff, dUnit: 'day' } };
        }
        else if ([types_1.DateFormat['YYYY-MM'], types_1.DateFormat['MM']].includes(dateFormat)) {
            // When it comes to the month, the filled date defaults to the 1st of the month
            const diff = (0, dayjs_1.default)(_nums[1]).diff(_nums[0], 'month');
            for (let i = 2; i < _nums.length; i++) {
                const newDiff = (0, dayjs_1.default)(_nums[i]).diff(_nums[i - 1], 'month');
                if (Math.abs(newDiff - diff) > DELTA) {
                    return { type: 'copy' };
                }
            }
            return { type: 'dateArithmetic', args: { dDiff: diff, dUnit: 'month' } };
        }
        else if (dateFormat === types_1.DateFormat['YYYY']) { // When it comes to the year, the filled date defaults to January 1 of the current year
            const diff = (0, dayjs_1.default)(_nums[1]).diff(_nums[0], 'year');
            for (let i = 2; i < _nums.length; i++) {
                const newDiff = (0, dayjs_1.default)(_nums[i]).diff(_nums[i - 1], 'year');
                if (Math.abs(newDiff - diff) > DELTA) {
                    return { type: 'copy' };
                }
            }
            return { type: 'dateArithmetic', args: { dDiff: diff, dUnit: 'year' } };
        }
    }
    return { type: 'copy' };
}
exports.fillDataToCell = {
    undoable: true,
    execute: (context, options) => {
        const { model: state, fieldMapSnapshot } = context;
        const { selectionRange, fillRange, direction } = options;
        const datasheetId = store_1.Selectors.getActiveDatasheetId(state);
        if (!state || !datasheetId || !fillRange) {
            return null;
        }
        const snapshot = store_1.Selectors.getSnapshot(state, datasheetId);
        const actions = [];
        const linkedActions = [];
        function copyCol(selectCells, fillCells) {
            const data = [];
            for (let i = 0; i < fillCells.length; i++) {
                const cellIndex = i % selectCells.length;
                const selectCell = selectCells[cellIndex];
                const fillCell = fillCells[i];
                const fieldId = fillCell.fieldId;
                const field = store_1.Selectors.getField(state, fieldId, datasheetId);
                let cellValue = store_1.Selectors.getCellValue(state, snapshot, selectCell.recordId, selectCell.fieldId);
                cellValue = (0, model_1.handleEmptyCellValue)(cellValue, model_1.Field.bindContext(field, state).basicValueType);
                data.push({
                    recordId: fillCell.recordId,
                    fieldId: fillCell.fieldId,
                    value: cellValue,
                });
            }
            return data;
        }
        function arithmeticCol(diff, selectCellCol, fillRangeCells, unit) {
            const data = [];
            const lastCell = selectCellCol.slice(-1)[0];
            const base = store_1.Selectors.getCellValue(state, snapshot, lastCell.recordId, lastCell.fieldId);
            for (let i = 0; i < fillRangeCells.length; i++) {
                const cell = fillRangeCells[i];
                let value;
                if (unit === 'time') {
                    value = diff * (i + 1) + base;
                }
                else if (unit === 'day') {
                    value = (0, dayjs_1.default)((0, dayjs_1.default)(base).format('YYYY/MM/DD')).add(diff * (i + 1), 'day').valueOf();
                }
                else if (unit === 'month') {
                    value = (0, dayjs_1.default)((0, dayjs_1.default)(base).format('YYYY/MM')).add(diff * (i + 1), 'month').valueOf();
                }
                else if (unit === 'year') {
                    value = (0, dayjs_1.default)((0, dayjs_1.default)(base).format('YYYY')).add(diff * (i + 1), 'year').valueOf();
                }
                else {
                    value = diff * (i + 1) + base;
                }
                data.push({
                    recordId: cell.recordId,
                    fieldId: cell.fieldId,
                    value: value,
                });
            }
            return data;
        }
        // Calculate vertically filled data
        const computeFillDataVertical = (fillRangeCells, selectRangeCells, direction) => {
            const data = [];
            const tSelectRangeCells = (0, utils_1.transpose)(selectRangeCells);
            const tFillRangeCells = (0, utils_1.transpose)(fillRangeCells);
            for (let j = 0; j < tSelectRangeCells.length; j++) { // Calculate by column
                const tSelectCellCol = tSelectRangeCells[j];
                const tFillCellCol = tFillRangeCells[j];
                if (direction === model_1.FillDirection.Top) {
                    tSelectCellCol.reverse();
                    tFillCellCol.reverse();
                }
                const fieldId = tSelectCellCol[0].fieldId;
                const field = store_1.Selectors.getField(state, fieldId, datasheetId);
                // Determine the column type, find rules for time and numbers
                if (field.type === types_1.FieldType.Number || field.type === types_1.FieldType.DateTime) {
                    const pattern = patternFinder(tSelectCellCol, field.type, state, snapshot, field);
                    switch (pattern.type) {
                        case 'arithmetic':
                            const { diff } = pattern.args;
                            const colData = arithmeticCol(diff, tSelectCellCol, tFillCellCol);
                            data.push(...colData);
                            break;
                        case 'dateArithmetic':
                            const { dDiff, dUnit } = pattern.args;
                            const dColData = arithmeticCol(dDiff, tSelectCellCol, tFillCellCol, dUnit);
                            if (dColData.find(data => data.value < EARLIEST_DATE)) {
                                data.push(...copyCol(tSelectCellCol, tFillCellCol));
                            }
                            else {
                                data.push(...dColData);
                            }
                            break;
                        case 'copy':
                            data.push(...copyCol(tSelectCellCol, tFillCellCol));
                            break;
                    }
                }
                else {
                    data.push(...copyCol(tSelectCellCol, tFillCellCol));
                }
            }
            return data;
        };
        const updateFillFieldsProperty = (selectFields, fillFields, selectCells) => {
            const newFillFields = [];
            for (const [index, field] of fillFields.entries()) {
                const selectField = selectFields[index % selectFields.length];
                const stdValues = selectCells.filter(cell => cell.fieldId === selectField.id).map(cell => {
                    let cellValue = store_1.Selectors.getCellValue(state, snapshot, cell.recordId, cell.fieldId);
                    cellValue = (0, model_1.handleEmptyCellValue)(cellValue, model_1.Field.bindContext(selectField, state).basicValueType);
                    return model_1.Field.bindContext(selectField, state).cellValueToStdValue(cellValue);
                });
                const newField = (0, utils_1.fastCloneDeep)(field);
                const newProperty = newField.type === types_1.FieldType.Member ? newField.property :
                    model_1.Field.bindContext(newField, state).enrichProperty(stdValues);
                const data = Object.assign(Object.assign({}, newField), { property: newProperty });
                newFillFields.push(data);
                const rst = set_field_attr_1.setFieldAttr.execute(context, {
                    cmd: commands_1.CollaCommandName.SetFieldAttr,
                    fieldId: newField.id,
                    data,
                });
                if (rst && rst.result === command_manager_1.ExecuteResult.Success) {
                    actions.push(...rst.actions);
                }
            }
            return newFillFields;
        };
        // Calculate horizontally filled data
        const computeFillDataHorizontal = (fillRangeCells, selectRangeCells, direction) => {
            const data = [];
            const selectCells = (0, lodash_1.zip)(...selectRangeCells).flat();
            const fillCells = (0, lodash_1.zip)(...fillRangeCells).flat();
            if (direction === model_1.FillDirection.Left) {
                selectCells.reverse();
                fillCells.reverse();
            }
            // Horizontal padding, may need to extend the property of the field
            const fillFields = store_1.Selectors.getRangeFields(state, fillRange, datasheetId);
            let newFillFields = fillFields;
            const selectFields = store_1.Selectors.getRangeFields(state, selectionRange[0], datasheetId);
            newFillFields = updateFillFieldsProperty(selectFields, fillFields, selectCells);
            for (let i = 0; i < fillCells.length; i++) {
                const cellIndex = i % selectCells.length;
                const selectCell = selectCells[cellIndex];
                const fillCell = fillCells[i];
                const fillField = newFillFields.find(f => f.id === fillCell.fieldId);
                const selectField = store_1.Selectors.getField(state, selectCell.fieldId, datasheetId);
                let selectCellValue = store_1.Selectors.getCellValue(state, snapshot, selectCell.recordId, selectCell.fieldId);
                selectCellValue = (0, model_1.handleEmptyCellValue)(selectCellValue, model_1.Field.bindContext(selectField, state).basicValueType);
                const selectStdVal = model_1.Field.bindContext(selectField, state).cellValueToStdValue(selectCellValue);
                const willFillCellValue = model_1.Field.bindContext(fillField, state).stdValueToCellValue(selectStdVal);
                // Horizontal padding involves field conversion, select cv > stdVal > Fill cv
                data.push({
                    recordId: fillCell.recordId,
                    fieldId: fillCell.fieldId,
                    value: willFillCellValue,
                });
            }
            return data;
        };
        const fillDataToCell = (selectionRange, fillRange, direction) => {
            if (!fillRange || !selectionRange) {
                return;
            }
            const selectionRangeCells = store_1.Selectors.getCellMatrixFromRange(state, selectionRange[0]);
            const fillRangeCells = store_1.Selectors.getCellMatrixFromRange(state, fillRange);
            if (!(selectionRangeCells && fillRangeCells)) {
                return;
            }
            let data = [];
            switch (direction) {
                case model_1.FillDirection.Below:
                case model_1.FillDirection.Top:
                    data = computeFillDataVertical(fillRangeCells, selectionRangeCells, direction);
                    break;
                case model_1.FillDirection.Right:
                case model_1.FillDirection.Left:
                    data = computeFillDataHorizontal(fillRangeCells, selectionRangeCells, direction);
                    break;
                default:
                    return;
            }
            if (data.length) {
                const rst = set_records_1.setRecords.execute(context, {
                    cmd: commands_1.CollaCommandName.SetRecords,
                    data,
                });
                if (rst) {
                    if (rst.result === command_manager_1.ExecuteResult.Fail) {
                        return rst;
                    }
                    actions.push(...rst.actions);
                    Object.assign(fieldMapSnapshot, rst.fieldMapSnapshot);
                    linkedActions.push(...(rst.linkedActions || []));
                }
            }
            return;
        };
        fillDataToCell(selectionRange, fillRange, direction);
        if (actions.length === 0) {
            return null;
        }
        return {
            result: command_manager_1.ExecuteResult.Success,
            resourceId: datasheetId,
            resourceType: types_1.ResourceType.Datasheet,
            actions,
            linkedActions,
            fieldMapSnapshot
        };
    },
};
//# sourceMappingURL=fill_data_to_cells.js.map